import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input-simple';
import { Button } from '../../components/ui/button-simple';
import { Search, MapPin, Users, Eye, AlertCircle, ChevronLeft, ChevronRight, X, Building2, Sprout, Award, Plus, MessageSquare } from 'lucide-react';
import { municipalities } from '../../data/municipalities';
import { farmersAPI } from '../../services/api';
import { useFarmers } from '../../hooks/useApi';
import { RequestPostingModal } from '../../components/forms/RequestPostingModal';

// Add animations
const styles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in-up { animation: fadeInUp 0.5s ease-out forwards; }
  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.4s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.5s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.6s; }

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .animate-slide-down { animation: slideDown 0.6s ease-out forwards; }

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .animate-slide-up { animation: slideUp 0.4s ease-out forwards; }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-user-farmers-animations]')) {
    styleSheet.setAttribute('data-user-farmers-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export function UserFarmersDirectory({ user }) {
  const navigate = useNavigate();
  const { farmers, loading, error, fetchFarmers } = useFarmers();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('all');
  const [selectedOwnership, setSelectedOwnership] = useState('all');
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [showPostingModal, setShowPostingModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchFarmers(() => farmersAPI.getAll());
  }, [fetchFarmers]);

  const publicFarmers = useMemo(() => {
    const isMemberRole = user?.role === 'admin' || user?.role === 'farmer' || user?.role === 'processor';
    return farmers.map((farmer, index) => ({
      ...farmer,
      displayName: isMemberRole ? (farmer.name || `Farmer ${index + 1}`) : `Farmer ${index + 1}`,
      displayAddress: farmer.municipality || farmer.address || 'Bohol'
    }));
  }, [farmers, user]);

  const filteredFarmers = useMemo(() => {
    return publicFarmers.filter(farmer => {
      if (farmer.id === null || farmer.id === undefined) return false;
      const matchesSearch = (farmer.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (farmer.municipality || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMunicipality = selectedMunicipality === 'all' || farmer.municipality === selectedMunicipality;
      const matchesOwnership = selectedOwnership === 'all' || (farmer.landOwnership || '').toLowerCase().includes(selectedOwnership.toLowerCase());
      return matchesSearch && matchesMunicipality && matchesOwnership;
    });
  }, [publicFarmers, searchTerm, selectedMunicipality, selectedOwnership]);

  const paginatedFarmers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredFarmers.slice(startIndex, endIndex);
  }, [filteredFarmers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredFarmers.length / itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedMunicipality, selectedOwnership]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/50 via-orange-50/30 to-amber-100/40 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Banner */}
        <div className="mb-8 bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 rounded-2xl shadow-xl p-6 text-white animate-slide-down relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-md p-3.5 rounded-2xl shadow-inner">
                <Users className="h-8 w-8 text-amber-200" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-extrabold tracking-tight">Cacao Farmers Directory</h1>
                  <span className="bg-amber-500/30 text-amber-100 border border-amber-400/30 text-xs px-2.5 py-0.5 rounded-full font-medium">
                    Public View
                  </span>
                </div>
                <p className="text-amber-100 max-w-2xl text-xs mt-1 leading-relaxed">
                  Registered cacao farmers across Bohol. Basic agricultural and farm profiles are displayed below.
                </p>
              </div>
            </div>
            
            {/* Request to Post Info Button for Farmer Users */}
            {user?.role === 'farmer' && (
              <Button
                onClick={() => setShowPostingModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-2 px-5 py-3 rounded-xl shadow-lg border border-emerald-500/40 transition-all hover:scale-105"
              >
                <Plus className="h-4 w-4" />
                Request to Post My Info
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-5 rounded-2xl shadow-md mb-8 border border-amber-100/80">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search farmers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 rounded-xl border-gray-200 text-xs"
              />
            </div>

            <div className="relative">
              <MapPin className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
              <select 
                value={selectedMunicipality} 
                onChange={(e) => setSelectedMunicipality(e.target.value)}
                className="flex h-10 w-full rounded-xl border border-gray-200 bg-white px-3 pl-10 text-xs text-gray-700 outline-none"
              >
                <option value="all">All Municipalities</option>
                {municipalities.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="relative">
              <Building2 className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
              <select 
                value={selectedOwnership} 
                onChange={(e) => setSelectedOwnership(e.target.value)}
                className="flex h-10 w-full rounded-xl border border-gray-200 bg-white px-3 pl-10 text-xs text-gray-700 outline-none"
              >
                <option value="all">All Ownership Types</option>
                <option value="Owned">Owned</option>
                <option value="Bought">Bought</option>
                <option value="Inherited">Inherited</option>
                <option value="Caretaker">Caretaker</option>
                <option value="Tenanted">Tenanted</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mr-3"></div>
            <span className="text-sm font-medium text-gray-600">Loading directory...</span>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6 text-center">
            <AlertCircle className="h-6 w-6 text-rose-500 mx-auto mb-2" />
            <p className="text-sm text-rose-800 font-semibold">{error}</p>
          </div>
        )}

        {/* Farmers Grid */}
        {!loading && !error && filteredFarmers.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedFarmers.map((farmer) => (
                <div key={farmer.id} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden flex flex-col animate-fade-in-up">
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 border-b border-amber-100">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-bold text-amber-950">{farmer.displayName}</h3>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-200/60 text-amber-900 uppercase">
                        Farmer Profile
                      </span>
                    </div>
                    <div className="flex items-center mt-1 text-xs text-gray-600">
                      <MapPin className="h-3.5 w-3.5 mr-1 text-amber-600" />
                      <span>{farmer.displayAddress}</span>
                    </div>
                  </div>

                  <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between items-center p-2 bg-gray-50 rounded-xl">
                        <span className="text-gray-500 font-medium flex items-center gap-1.5"><Sprout className="w-3.5 h-3.5 text-green-600" /> Farm Size</span>
                        <span className="font-bold text-gray-800">{farmer.farmSize || farmer.farmAreaHa || 'N/A'} ha</span>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-gray-50 rounded-xl">
                        <span className="text-gray-500 font-medium flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-blue-600" /> Ownership</span>
                        <span className="font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md">{farmer.landOwnership || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-gray-50 rounded-xl">
                        <span className="text-gray-500 font-medium flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-amber-600" /> Variety</span>
                        <span className="font-semibold text-gray-800">{farmer.varietyPlanted || (farmer.cacaoVarieties || []).join(', ') || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => setSelectedFarmer(farmer)}
                        className="flex-1 bg-gradient-to-r from-amber-700 to-amber-800 hover:from-amber-800 hover:to-amber-900 text-white font-bold text-xs py-2.5 rounded-xl shadow transition flex items-center justify-center gap-1"
                      >
                        <Eye className="w-3.5 h-3.5" /> View Profile
                      </Button>
                      {(user?.role === 'admin' || user?.role === 'farmer' || user?.role === 'processor') && (
                        <Button
                          onClick={() => navigate(`/messages?partnerId=farmer_${farmer.id}&partnerName=${encodeURIComponent(farmer.displayName || farmer.name || 'Farmer')}&partnerRole=farmer`)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3 py-2.5 rounded-xl shadow transition flex items-center justify-center gap-1"
                          title="Message Farmer"
                        >
                          <MessageSquare className="w-3.5 h-3.5" /> Message
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8 bg-white p-4 rounded-2xl shadow border border-amber-100">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 text-xs px-4 py-2 rounded-xl disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <span className="text-xs text-gray-600 font-semibold">Page {currentPage} of {totalPages}</span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 text-xs px-4 py-2 rounded-xl disabled:opacity-50"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Selected Farmer Modal */}
        {selectedFarmer && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 animate-fade-in backdrop-blur-sm">
            <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl animate-slide-up">
              <div className="bg-gradient-to-r from-amber-700 to-amber-800 p-5 text-white flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-bold">{selectedFarmer.displayName}</h2>
                  <p className="text-amber-100 text-xs">{selectedFarmer.displayAddress}</p>
                </div>
                <button onClick={() => setSelectedFarmer(null)} className="text-white/80 hover:text-white p-1.5 rounded-xl hover:bg-white/10">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-3 text-xs">
                <div className="bg-gray-50 p-3 rounded-2xl space-y-2">
                  <div className="flex justify-between"><span className="text-gray-500">Municipality:</span><span className="font-semibold">{selectedFarmer.displayAddress}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Farm Size:</span><span className="font-semibold">{selectedFarmer.farmSize || selectedFarmer.farmAreaHa || 'N/A'} ha</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Land Ownership:</span><span className="font-semibold">{selectedFarmer.landOwnership || 'N/A'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Varieties Planted:</span><span className="font-semibold">{selectedFarmer.varietyPlanted || (selectedFarmer.cacaoVarieties || []).join(', ') || 'N/A'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Actual Trees:</span><span className="font-semibold">{selectedFarmer.actualNumberOfTreesPlanted || 'N/A'}</span></div>
                </div>
                <Button onClick={() => setSelectedFarmer(null)} className="w-full bg-amber-700 text-white font-bold py-2.5 rounded-xl">
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Posting Request Modal for Farmer role */}
        {showPostingModal && user && (
          <RequestPostingModal
            user={user}
            role="farmer"
            onClose={() => setShowPostingModal(false)}
            onSuccess={() => setShowPostingModal(false)}
          />
        )}

      </div>
    </div>
  );
}
